import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import cx from 'classnames';

// Design System
import { Modal } from 'jl-design-system/components/modal/Modal';
import { PrimaryButton, SecondaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectCutOffPassedModalProps } from './ClickAndCollectCutOffPassedModal.types';

// Config
import clickAndCollectCutOffPassedModalState from './ClickAndCollectCutOffPassedModal.state';
import { useWindowSize } from '@context/window/WindowSizeContext';
import {
  changeClickAndCollectSelectedPoint,
  handleCutOffPassedModalChangeDateClick,
  submitCCDeliveryDetails,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import getClickCollectFormattedTime from '@utils/helpers/time';
import { isCollectPlusRelatedType } from '@utils/collection/getCollectPlusRelatedStores';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';

// Components
import Container from '@components/container';

// Styles
import styles from './click-and-collect-cut-off-passed-modal.scss';

const ClickAndCollectCutOffPassedModal = ({
  handleSubmit = () => {},
  isPayment = false,
}: ClickAndCollectCutOffPassedModalProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    collectorDetails,
    cutOffTime = '',
    giftMessageLines,
    proceedingToPayment,
    selectedCollectionPoint = {},
    showModal,
    suggestedNewCollectionDate = {},
  } = useSelector(clickAndCollectCutOffPassedModalState);
  const { isDesktop } = useWindowSize();

  useEffect(() => {
    document.addEventListener('keydown', disabledEscKeyHandler);

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler);
    };
  }, []);

  if (!showModal) {
    return null;
  }

  const handleContinue = async () => {
    if (isPayment) {
      const body = {
        collectorDetails,
        fulfilment: {
          fulfilmentOfferId: suggestedNewCollectionDate.fulfilmentOfferId,
          giftMessageLines,
        },
      };

      await dispatch(submitCCDeliveryDetails(body));
    }

    handleSubmit();
  };

  const handleChangeDateClick = () => dispatch(handleCutOffPassedModalChangeDateClick({ isPayment }));

  const handleChangeShopClick = () => dispatch(changeClickAndCollectSelectedPoint());

  const handleFormattedTime = (canShowFormattedTime: boolean, date: any) => {
    if (canShowFormattedTime) {
      return null;
    }

    return ` from ${getClickCollectFormattedTime({
      time: date,
      bold: false,
      timeOnly: true,
    })}`;
  };

  const isCollectPlusRelatedOwners =
    selectedCollectionPoint && selectedCollectionPoint.ownerId
      ? isCollectPlusRelatedType(selectedCollectionPoint.ownerId)
      : false;
  const continueButtonText = isPayment ? 'Place your order' : 'Continue to payment';

  return (
    <Modal className={cx(styles.modal)} footer={false} isOpen title="Your collection date has changed">
      <p>The {format(cutOffTime, 'h:mma')} cut-off time has now passed.</p>
      <p>
        Your item(s) will be ready for collection
        {handleFormattedTime(isCollectPlusRelatedOwners, suggestedNewCollectionDate.date)} on{' '}
        <strong>
          {suggestedNewCollectionDate && suggestedNewCollectionDate.date
            ? format(suggestedNewCollectionDate?.date, 'dddd D MMMM YYYY')
            : ''}
        </strong>{' '}
        when you complete the order.
      </p>

      <Container
        flex={{
          alignItems: 'center',
          flexDirection: isDesktop ? 'row' : 'col-reverse',
          flexWrap: 'wrap',
          justifyContent: isDesktop ? 'end' : 'start',
        }}
        marginTop="3"
      >
        {!isCollectPlusRelatedOwners && (
          <SecondaryButton
            data-testid="click-collect-cut-off-passed-modal-change-date"
            inline={isDesktop}
            onClick={handleChangeDateClick}
          >
            Change collection date
          </SecondaryButton>
        )}

        {isCollectPlusRelatedOwners && (
          <SecondaryButton
            data-testid="click-collect-cut-off-passed-modal-change-store"
            inline={isDesktop}
            onClick={handleChangeShopClick}
          >
            Change shop
          </SecondaryButton>
        )}

        <PrimaryButton
          data-testid="click-collect-cut-off-passed-modal-continue"
          inline={isDesktop}
          onClick={handleContinue}
          submitting={proceedingToPayment}
        >
          {continueButtonText}
        </PrimaryButton>
      </Container>
    </Modal>
  );
};

export default ClickAndCollectCutOffPassedModal;
